import type { AccountRiskProfile, RiskAssessmentResult } from '@gcv/shared';
import { useQueryClient } from '@tanstack/react-query';

import {
  ACCOUNT_RISK_PROFILE_KEY,
  RISK_ASSESSMENT_TEMPLATES_KEY,
  RISK_ASSESSMENTS_KEY,
  RISK_BANDS_KEY
} from 'tanstack-hooks/keys';
import { useUser } from 'tanstack-hooks/user';

/**
 * Keeps risk assessment, template, band set, and account risk profile caches in sync after mutations
 */
export function useFiRiskAssessmentCache() {
  const { userBankId } = useUser();
  const queryClient = useQueryClient();

  const invalidateRiskAssessments = () =>
    queryClient.invalidateQueries({ queryKey: [RISK_ASSESSMENTS_KEY, userBankId] });

  const setRiskAssessment = (result: RiskAssessmentResult) =>
    queryClient.setQueryData([RISK_ASSESSMENTS_KEY, userBankId, 'detail', result.id], result);

  const invalidateRiskAssessmentTemplates = () =>
    queryClient.invalidateQueries({ queryKey: [RISK_ASSESSMENT_TEMPLATES_KEY, userBankId] });

  const invalidateRiskBandSets = () =>
    queryClient.invalidateQueries({ queryKey: [RISK_BANDS_KEY, userBankId] });

  const invalidateAccountRiskProfile = (dispensaryId: string) =>
    queryClient.invalidateQueries({ queryKey: [ACCOUNT_RISK_PROFILE_KEY, userBankId, dispensaryId] });

  const setAccountRiskProfile = (dispensaryId: string, profile: AccountRiskProfile) =>
    queryClient.setQueryData([ACCOUNT_RISK_PROFILE_KEY, userBankId, dispensaryId], profile);

  return {
    invalidateAccountRiskProfile,
    invalidateRiskAssessments,
    invalidateRiskAssessmentTemplates,
    invalidateRiskBandSets,
    setAccountRiskProfile,
    setRiskAssessment
  };
}
